<template>
  <div class="p-5 pt-2">
    <!-- 表格数据 -->
    <el-table class="normal-table" :style="{ maxHeight: '300px', borderBottom: isNull(tableData) || tableData.length > 0 ? 'none' : '1px solid var(--color-split-line) !important'}" :border="true" :data="tableData">
      <el-table-column align="left" prop="type" label="审批类型" :min-width="200">
        <template #default="scope">
          {{ approveTypeLabel[scope.row.type] }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="submitTime" show-overflow-tooltip label="提交时间" :min-width="200" />
      <el-table-column align="left" prop="confirmTime" :formatter="(row) => isNull(row.confirmTime) ? '--' : row.confirmTime" show-overflow-tooltip label="审批日期" :min-width="200" />
      <el-table-column align="center" prop="status" label="审批状态" :min-width="150">
        <template #default="scope">
          <custom-tag :type="approveStatusStyle[scope.row.status]" :label="approveStatusLabel[scope.row.status]" />
        </template>
      </el-table-column>
      <el-table-column align="left" prop="remark" show-overflow-tooltip label="审批备注" :min-width="200">
        <template #default="scope">
          {{ scope.row.remark || '--' }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="auditor" label="审批人" :min-width="150" :formatter="(row) => isNull(row.auditor) ? '--' : row.auditor" />
      <el-table-column align="center" fixed="right" prop="taskNo" label="操作" :min-width="100">
        <template #default="scope">
          <el-button link type="primary" @click="openApproverConfig(scope.row)">
            详情
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script setup lang="ts">
import { onMounted, PropType, reactive, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { CustomTag } from '@vue3/components';
import { IApproveManageParam, IApproveTaskRes } from '@core/main/types/system/approval';
import { approveStatusLabel, approveStatusStyle, approveTypeLabel, EApproveType, IBusinessLineItem } from '@core/main/types/common';
import { useRouter, useRoute } from 'vue-router';
import { queryBusinessLineList, queryInitiateApprovList } from '@core/main/api';
import { isNull } from '@core/tools';

const props = defineProps({
  isCompanyIdentity: {
    type: Boolean,
    default: false,
  },
  pageParams: {
    type: Object as PropType<{ page: number; size: number }>,
    default: () => ({
      page: 1,
      size: 6,
    }),
  },
});

const emit = defineEmits(['update:total', 'update:loading']);

const router = useRouter();
const route = useRoute();

const params = reactive<IApproveManageParam>({
  page: 1,
  size: props.pageParams.size,
});

const businessLineList = ref<IBusinessLineItem[]>([]);
const tableData = ref<IApproveTaskRes[]>([]);
const submitDateRange = ref<string[]>([]);
const confirmDateRange = ref<string[]>([]);
const activeIndex = ref(route.query && route.query.activeIndex ? Number(route.query.activeIndex) : 1);

function openApproverConfig(row: IApproveTaskRes) {
  router.push({
    name: 'ApprovalForm',
    query: {
      taskNo: row.taskNo,
      type: row.type,
      refId: row.type === EApproveType.APPLET_PUB ? row.agentId : row.refId,
      companyId: row.type === EApproveType.TAX_SOURCE || row.type === EApproveType.EDIT_TAX_SOURCE ? row.companyId : undefined,
      serverId: row.type === EApproveType.SERVER_TAX_SOURCE || row.type === EApproveType.SERVER_EDIT_TAX_SOURCE ? row.serverId : undefined,
      openType: 'detail',
      activeIndex: activeIndex.value,
    },
    state: row?.extraInfo ? JSON.parse(String(row?.extraInfo)) : undefined,
  });
}

async function fetchData() {
  emit('update:loading', true);
  try {
    const { data, total: all } = await queryInitiateApprovList({
      ...params,
      submitTimeFrom: submitDateRange.value ? submitDateRange.value[0] : undefined,
      submitTimeTo: submitDateRange.value ? submitDateRange.value[1] : undefined,
      confirmTimeFrom: confirmDateRange.value ? confirmDateRange.value[0] : undefined,
      confirmTimeTo: confirmDateRange.value ? confirmDateRange.value[1] : undefined,
    });

    tableData.value = data;
    emit('update:total', all);
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  emit('update:loading', false);
}

async function fetchBusinessLineList() {
  try {
    businessLineList.value = await queryBusinessLineList();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = undefined;
    }
  }
}

onMounted(() => {
  resetForm();
  fetchData();
  fetchBusinessLineList();
});

watch(
  () => props.pageParams,
  (val) => {
    if (val.page !== params.page) {
      params.page = val.page;
      fetchData();
    }
  },
  { deep: true },
);
</script>

<style lang="scss" scoped>
:deep(.normal-table) {
  border-bottom: none !important;

  .el-table__cell {
    @apply py-2 !important;
  }

  tbody {
    .el-table__cell {
      @apply py-[7.5px] !important;
    }
  }

  tbody {
    .el-table__row {
      &:last-child {
        td.el-table__cell {
          border-bottom: 1px solid var(--color-split-line) !important;
        }
      }
    }
  }
}
</style>

